/**
 * FE-204 — Pure derivation of the "Borrowed from X" / "Lent to X"
 * pill state for a card in the Items grid.
 *
 * Why this exists: ItemGridCard inside BrowseScreen.tsx is React.memo'd
 * and the truth table for which pill renders is a few lines of Boolean
 * logic on `user_id` (owner) and `current_custodian_user_id` (current
 * holder). Lifting that into a pure function keeps the component file
 * shorter and gives us a small, fast unit-test surface — mounting the
 * full BrowseScreen for a 4-state truth table would be way over-budget.
 *
 * Truth table:
 *
 *   viewerId | ownerId | custodianId | result
 *   ─────────┼─────────┼─────────────┼──────────────
 *   null     | *       | *           | NONE     ← logged-out / hydrating
 *   42       | 42      | 42          | NONE     ← owned and in possession
 *   42       | 42      | null        | NONE     ← owned, custodian not set
 *                                                  (legacy backfill row)
 *   42       | 42      | 99          | LENT_OUT ← I own it, someone else
 *                                                  is currently holding it
 *   42       | 99      | 42          | BORROWED ← I'm holding it but
 *                                                  I don't own it
 *   42       | 99      | 99          | NONE     ← not mine, not with me
 *                                                  (we shouldn't see this
 *                                                   row in inventory until
 *                                                   BE-210 ships
 *                                                   `held_by_me=true`)
 */

export type ItemPossessionState =
  | { kind: 'NONE' }
  | { kind: 'BORROWED'; counterpartyUserId: number }
  | { kind: 'LENT_OUT'; counterpartyUserId: number };

export function deriveItemPossessionState(input: {
  viewerUserId: number | null | undefined;
  ownerUserId: number | null | undefined;
  custodianUserId: number | null | undefined;
  /** FE-248 v2 follow-up — the BE response is inconsistent across
   *  custody paths (filed as backend#248 / SG-26). Some "I'm holding
   *  this" rows come back without `current_custodian_user_id` set but
   *  WITH `sharing_info.is_borrowed === true`. When the canonical
   *  custody fields aren't populated but this flag is, we still want
   *  to render the BORROWED pill so the user sees a consistent
   *  badge across cards. counterpartyUserId is unknown in this case;
   *  rendering layer should show generic "Borrowed" copy. */
  isBorrowedFlag?: boolean;
}): ItemPossessionState {
  const { viewerUserId, ownerUserId, custodianUserId, isBorrowedFlag } = input;
  if (viewerUserId == null) return { kind: 'NONE' };

  // Canonical custody path (both ids present).
  if (
    ownerUserId != null &&
    custodianUserId != null &&
    custodianUserId !== ownerUserId
  ) {
    if (custodianUserId === viewerUserId && ownerUserId !== viewerUserId) {
      return { kind: 'BORROWED', counterpartyUserId: ownerUserId };
    }
    if (ownerUserId === viewerUserId && custodianUserId !== viewerUserId) {
      return { kind: 'LENT_OUT', counterpartyUserId: custodianUserId };
    }
  }

  // Fallback: BE response only set `sharing_info.is_borrowed` (custody
  // ids were dropped en route through some serializer path). Treat as
  // BORROWED with unknown counterparty.
  if (isBorrowedFlag === true) {
    // Use sentinel -1 so the rendering layer can detect "unknown" and
    // show generic copy instead of a (Resolver: -1) → "Member" mess.
    return { kind: 'BORROWED', counterpartyUserId: -1 };
  }

  return { kind: 'NONE' };
}
